import _ from "lodash";

import { ActionTypeCode, AdjustedProductType, DepositDetail, FundAction, FundTransferDetails, Gender, InvestmentPathDetail, LiquidityDetails, LiquidityMatchedDepositCap, LiquidityPeriodBalanceType, LiquiditySummary, LoanDetails, Mimshak, PolicyOrAccount, PolicyStatus, Product, ProductType } from "../interfaces/DTO/mislaka_xml_models";
import { FundDataBasic } from "../interfaces/DTO/FundDataBasic";
import { ManagementFeeDetails, PensionFeeExpenseType, PolicyEmploymentStatus } from "../interfaces/DTO/mislaka_xml_pension_models";
import { MimshakData } from "../interfaces/DTO/mimshakData";



export class MimshakService {
  // Static arrays for pension funds filtering
  public static readonly pensionFundsTypes = [
    ProductType.PENSION_FUND,
    ProductType.PROVIDENT_FUND,
    ProductType.PURE_SAVINGS_POLICY,
    ProductType.LIFE_INSURANCE_SAVINGS,
  ];

  public static readonly pensionFundsTypesByAdjustedProductType = [
    AdjustedProductType.ACTIVE_MANAGERS_INSURANCE,
    AdjustedProductType.INACTIVE_MANAGERS_INSURANCE,

    AdjustedProductType.PENSION_FUND,
    AdjustedProductType.MANDATORY_PENSION_FUND,
    AdjustedProductType.SUPPLEMENTARY_PENSION_FUND,
    AdjustedProductType.BASIC_PENSION_FUND,

    AdjustedProductType.PROVIDENT_FUND,
    AdjustedProductType.CAPITAL_PROVIDENT_FUND,
    AdjustedProductType.PENSION_PROVIDENT_FUND,
    AdjustedProductType.COMBINED_PROVIDENT_FUND,
    AdjustedProductType.OLD_COMPREHENSIVE_PENSION,
    AdjustedProductType.OLD_BASIC_PENSION,
  ];

  // Static arrays for liquid funds filtering
  public static readonly liquidFundsTypes = [
    ProductType.EDUCATION_FUND,
    ProductType.PURE_SAVINGS_POLICY,
    ProductType.INVESTMENT_PROVIDENT_FUND,
    ProductType.CHILD_SAVINGS,
  
  ];

  public static readonly liquidFundsTypesByAdjustedProductType = [
    AdjustedProductType.EDUCATION_FUND,
    AdjustedProductType.CHILD_SAVINGS,
    AdjustedProductType.INVESTMENT_PROVIDENT_FUND,
    AdjustedProductType.PURE_SAVINGS_POLICY,
  ];

  // Static arrays for insurance/warranty cover funds filtering
  public static readonly insuranceFundsTypes = [
    ProductType.LIFE_INSURANCE_SAVINGS,
    ProductType.PURE_RISK_POLICY,
    ProductType.MORTGAGE_LIFE_INSURANCE,
    ProductType.COLLECTIVE_RISK_POLICY,
    ProductType.PENSION_FUND,
    ProductType.PURE_SAVINGS_POLICY,
  ];

  public static readonly insuranceFundsTypesByAdjustedProductType = [
    AdjustedProductType.ACTIVE_MANAGERS_INSURANCE,
    AdjustedProductType.INACTIVE_MANAGERS_INSURANCE,
    AdjustedProductType.PURE_RISK_POLICY,
    AdjustedProductType.MORTGAGE_LIFE_INSURANCE,
    AdjustedProductType.COLLECTIVE_RISK_POLICY,
    AdjustedProductType.PENSION_FUND,
    AdjustedProductType.MANDATORY_PENSION_FUND,
    AdjustedProductType.SUPPLEMENTARY_PENSION_FUND,
    AdjustedProductType.BASIC_PENSION_FUND,
    AdjustedProductType.OLD_COMPREHENSIVE_PENSION,
    AdjustedProductType.OLD_BASIC_PENSION,
  ];

  public static readonly gemelTypesByAdjustedProductType = [
    AdjustedProductType.PROVIDENT_FUND,
    AdjustedProductType.CAPITAL_PROVIDENT_FUND,
    AdjustedProductType.PENSION_PROVIDENT_FUND,
    AdjustedProductType.COMBINED_PROVIDENT_FUND,
  ];

  /** Product types whose deposits are reported by salary month rather than payment period. */
  public static readonly salaryMonthTypesByAdjustedProductType = new Set<AdjustedProductType>([
    ...MimshakService.pensionFundsTypesByAdjustedProductType,
    AdjustedProductType.EDUCATION_FUND,
  ]);

  /**
   * True for any type where regular deposits aren't expected — either because the product
   * isn't conceptually a recurring-deposit product (ad-hoc savings, risk-only policies), or
   * because deposit activity simply isn't reported to us for that type (e.g. insurance-only
   * types). A zero-deposit month for these should render as a neutral gap, not a flagged one.
   */
  static isNonRecurringDepositType(adjustedType: AdjustedProductType): boolean {
    return !MimshakService.salaryMonthTypesByAdjustedProductType.has(adjustedType);
  }

  /**
   * Normalizes a pension-related AdjustedProductType for grouping: inactive managers insurance
   * collapses into active, and any gemel variant collapses into the combined capital+pension type.
   */
  static normalizePensionType(t: AdjustedProductType): AdjustedProductType {
    if (t === AdjustedProductType.INACTIVE_MANAGERS_INSURANCE) return AdjustedProductType.ACTIVE_MANAGERS_INSURANCE;
    if (MimshakService.gemelTypesByAdjustedProductType.includes(t)) return AdjustedProductType.COMBINED_PROVIDENT_FUND;
    return t;
  }

  /**
   * Normalizes a capital-related AdjustedProductType for grouping: inactive managers insurance
   * collapses into active, and combined provident fund collapses into the capital-only gemel type.
   */
  static normalizeForCapital(t: AdjustedProductType): AdjustedProductType {
    if (t === AdjustedProductType.INACTIVE_MANAGERS_INSURANCE) return AdjustedProductType.ACTIVE_MANAGERS_INSURANCE;
    if (t === AdjustedProductType.COMBINED_PROVIDENT_FUND) return AdjustedProductType.CAPITAL_PROVIDENT_FUND;
    return t;
  }

//#region פילטרים
  static getAllPoliciesOrAccounts(products: Product[]): PolicyOrAccount[] {
    if (!products || products.length === 0) {
      return [];
    }

    // Flatten the policies_or_accounts from all products
    return products.flatMap((product) => product.policies_or_accounts || []);
  }
  static getAllPolicies(mimshaks: Mimshak[]): PolicyOrAccount[] {
    if (!mimshaks || mimshaks.length === 0) {
      return [];
    }
    const products = MimshakService.getAllProducts(mimshaks);
    return MimshakService.getAllPoliciesOrAccounts(products);
  }
  
 static filterLiquidProducts(mimshaks: Mimshak[]): Product[] {
  return mimshaks.flatMap((mimshak) =>
    mimshak.products.filter((product) => {
      const { product_type, adjusted_product_type } = product.details ?? {};

      // בדיקה בסיסית - אם product_type ריק או 0
      if (product_type == null || product_type === 0) {
        return false;
      }

      // אם יש adjusted_product_type, נבדוק אם הוא בתוך liquidFundsTypesByAdjustedProductType
      // אחרת, נבדוק אם product_type בתוך liquidFundsTypes
      const isValidType = adjusted_product_type != null
        ? MimshakService.liquidFundsTypesByAdjustedProductType.includes(adjusted_product_type)
        : MimshakService.liquidFundsTypes.includes(product_type);

      if (!isValidType) {
        return false;
      }

      const effectiveType =
        product_type === ProductType.PURE_SAVINGS_POLICY && adjusted_product_type != null
          ? adjusted_product_type
          : product_type;

      // במקרה של פוליסת חיסכון או קופת גמל – בדיקה פרטנית
      if (
        (effectiveType === ProductType.PURE_SAVINGS_POLICY ||
         effectiveType === ProductType.PROVIDENT_FUND) &&
        !MimshakService.isPureSavingsPolicyBelongsToLiquidFunds(product.policies_or_accounts)
      ) {
        return false;
      }

      return true;
    })
  );
}

static getAllProducts(mimshaks: Mimshak[]): Product[] {
  return mimshaks.flatMap((mimshak) =>
    mimshak.products
  );
}
static filterProductsByType(mimshaks: Mimshak[], productType: ProductType): Product[] {
  return mimshaks.flatMap((mimshak) =>
    mimshak.products.filter(
      (product) => product.details.product_type === productType
    )
  );
}
static filterProductsByType2(products: Product[], productType: ProductType): Product[] {
  return products.filter(product => product.details.product_type === productType);
}

/**
 * Filters education fund products by employment type and checks for deposits in the selected year.
 * @param products - List of all products
 * @param employmentType - EMPLOYEE or SELF_EMPLOYED
 * @param selectedYear - Year to check for deposits
 * @returns Filtered products with only policies matching the employment type and having deposits in the year
 */
static filterEducationFundsByEmploymentType(
  products: Product[],
  employmentType: PolicyEmploymentStatus,
  selectedYear: number
): Product[] {
  const educationFunds = MimshakService.filterProductsByType2(products, ProductType.EDUCATION_FUND);

  

  const filtered = educationFunds
    .map((product) => {
      // Filter policies by employment type
      const filteredPolicies = product.policies_or_accounts.filter((policy) => {
        if (employmentType === PolicyEmploymentStatus.EMPLOYEE) {
          return policy.plan_or_account_type === PolicyEmploymentStatus.EMPLOYEE;
        } else {
          // Self-employed includes both SELF_EMPLOYED and SELF_EMPLOYED_AND_EMPLOYEE
          return (
            policy.plan_or_account_type === PolicyEmploymentStatus.SELF_EMPLOYED ||
            policy.plan_or_account_type === PolicyEmploymentStatus.SELF_EMPLOYED_AND_EMPLOYEE
          );
        }
      });

      

      // Check if any of the filtered policies have deposits in the selected year
      const hasDepositsInYear = filteredPolicies.some((policy) => {
        const deposits = policy.deposits || [];
        const depositsInYear = deposits.filter((deposit) => {
          // Use salary_month if available, otherwise use payment_period
          let dateValue: number;
          if (deposit.salary_month && deposit.salary_month.trim() !== '') {
            dateValue = parseInt(deposit.salary_month); // e.g., 202501
          } else if (deposit.payment_period) {
            dateValue = deposit.payment_period; // e.g., 202501
          } else {
            return false;
          }

          // For a given year (e.g., 2025), only include deposits from January of that year onwards
          // i.e., salary_month/payment_period >= 202501 and < 202601
          const yearStart = selectedYear * 100 + 1; // e.g., 202501
          const nextYearStart = (selectedYear + 1) * 100 + 1; // e.g., 202601

          
          return dateValue >= yearStart && dateValue < nextYearStart;
        });
        
        return depositsInYear.length > 0;
      });

      

      // Return the product with filtered policies only if there are deposits
      if (hasDepositsInYear && filteredPolicies.length > 0) {
        return {
          ...product,
          policies_or_accounts: filteredPolicies,
        };
      }
      return null;
    })
    .filter((product): product is Product => product !== null);

  

  return filtered;
}

/**
 * Filters policies or accounts by their status.
 * @param products - List of products.
 * @param status - The status to filter by.
 * @returns Flat list of policies or accounts with the specified status.
 */
static filterPoliciesOrAccountsByStatus(products: Product[], status: PolicyStatus): PolicyOrAccount[] {
  return products.flatMap((product) =>
    (product.policies_or_accounts || []).filter((policy) => policy.status === status)
  );
}
static filterProductsByPolicyNumber(products: Product[], policyNumbers: string[]): Product[] {
  if (!policyNumbers.length) return products;

  return products
    .map(product => ({
      ...product,
      policies_or_accounts: product.policies_or_accounts.filter(policy =>
        policyNumbers.includes(policy.policy_number)
      )
    }))
    .filter(product => product.policies_or_accounts.length > 0); // להשאיר רק מוצרים עם פוליסות תואמות
}
static filterMimshaksByPolicyNumber(mimshaks: Mimshak[], policyNumbers: string[]): Product[] {
  if (!policyNumbers.length) {
    return mimshaks.flatMap(mimshak => mimshak.products);
  }

  return mimshaks.flatMap(mimshak => 
    this.filterProductsByPolicyNumber(mimshak.products, policyNumbers)
  );
}

static filterProductsByPolicyStatus(products: Product[], status: PolicyStatus): Product[] {
  return products.filter((product) =>
    (product.policies_or_accounts || []).some((policy) => policy.status === status)
  );
}

static getPoliciesOrAccountsFromLiquidFunds(mimshaks: Mimshak[]): PolicyOrAccount[] {
  // קבלת קרנות נזילות באמצעות הפונקציה הקיימת
  const liquidFunds = MimshakService.filterLiquidProducts(mimshaks);

  // שימוש בפונקציה החדשה כדי לשלוף ולסנן את הפוליסות/חשבונות
  return MimshakService.getPoliciesOrAccountsFromLiquidProducts(liquidFunds);
}

static getPoliciesOrAccountsFromLiquidProducts(products: Product[]): PolicyOrAccount[] {
  // שליפת כל הפוליסות/חשבונות מהמוצרים שהתקבלו
  const allPoliciesOrAccounts = MimshakService.getAllPoliciesOrAccounts(products);

  // סינון רק לפוליסות ששייכות לקרנות נזילות לפי isPureSavingsPolicyBelongsToLiquidFunds
  return allPoliciesOrAccounts.filter((policyOrAccount) =>
    MimshakService.isPureSavingsPolicyBelongsToLiquidFunds([policyOrAccount])
  );
}
static getPoliciesOrAccountsFromPensionProducts(products: Product[]): PolicyOrAccount[] {
  // שליפת כל הפוליסות/חשבונות מהמוצרים שהתקבלו
  const allPoliciesOrAccounts = MimshakService.getAllPoliciesOrAccounts(products);

  // סינון רק לפוליסות ששייכות לקרנות נזילות לפי isPureSavingsPolicyBelongsToLiquidFunds
  return allPoliciesOrAccounts.filter((policyOrAccount) =>
    MimshakService.isPureSavingsPolicyBelongsToPensionFunds([policyOrAccount])
  );
}

static getPoliciesOrAccountsFromPensionFunds(mimshaks: Mimshak[]): PolicyOrAccount[] {
  // קבלת קרנות פנסיה באמצעות הפונקציה הקיימת
  const pensionFunds = MimshakService.filterPensionFunds(mimshaks);

  // שימוש בפונקציה הגנרית להוצאת כל הפוליסות/חשבונות מתוך קרנות הפנסיה
  const allPoliciesOrAccounts = MimshakService.getAllPoliciesOrAccounts(pensionFunds);
    // סינון רק לפוליסות ששייכות לקרנות נזילות לפי isPureSavingsPolicyBelongsToLiquidFunds
  return allPoliciesOrAccounts.filter((policyOrAccount) =>
      MimshakService.isPureSavingsPolicyBelongsToPensionFunds([policyOrAccount])
    );
}

static isPureSavingsPolicyBelongsToLiquidFunds(policyOrAccounts: PolicyOrAccount[]): boolean {
  return policyOrAccounts.some((policy) =>
    policy.accumulated_savings.some(
      (saving) => saving.savings_type_code === LiquidityPeriodBalanceType.CAPITAL
    )
  );
}

static isPureSavingsPolicyBelongsToPensionFunds(policyOrAccounts: PolicyOrAccount[]): boolean {
  return policyOrAccounts.some((policy) =>
    policy.accumulated_savings.some(
      (saving) => saving.savings_type_code !== LiquidityPeriodBalanceType.CAPITAL
    )
  );
}

// פונקציה לסינון הפנסיה
static filterPensionFunds(mimshaks: Mimshak[]): Product[] {
  return mimshaks.flatMap((mimshak) =>
    mimshak.products.filter((product) => {
      const { product_type, adjusted_product_type } = product.details ?? {};

      // בדיקה בסיסית - אם product_type ריק או 0
      if (product_type == null || product_type === 0) {
        return false;
      }

      // אם יש adjusted_product_type, נבדוק אם הוא בתוך pensionFundsTypesByAdjustedProductType
      // אחרת, נבדוק אם product_type בתוך pensionFundsTypes
      const isValidType = adjusted_product_type != null
        ? MimshakService.pensionFundsTypesByAdjustedProductType.includes(adjusted_product_type)
        : MimshakService.pensionFundsTypes.includes(product_type);

      if (!isValidType) {
        return false;
      }

      const effectiveType =
        product_type === ProductType.PURE_SAVINGS_POLICY && adjusted_product_type != null
          ? adjusted_product_type
          : product_type;

      // בדיקה נוספת עבור PURE_SAVINGS_POLICY ו־PROVIDENT_FUND
      if (
        (effectiveType === ProductType.PURE_SAVINGS_POLICY || effectiveType === ProductType.PROVIDENT_FUND) &&
        !MimshakService.isPureSavingsPolicyBelongsToPensionFunds(product.policies_or_accounts)
      ) {
        return false;
      }

      return true;
    })
  );
}

 /**
   * Filters products that are unidentified.
   * @param mimshaks - List of Mimshak objects.
   * @returns Filtered list of unidentified products.
   */
 static filterWarrentyCoverFunds(mimshaks: Mimshak[]): Product[] {
  return mimshaks.flatMap((mimshak) =>
    mimshak.products.filter((product) => {
      const { product_type, adjusted_product_type } = product.details ?? {};

      // בדיקה בסיסית - אם product_type ריק או 0
      if (product_type == null || product_type === 0) {
        return false;
      }

      // אם יש adjusted_product_type, נבדוק אם הוא בתוך insuranceFundsTypesByAdjustedProductType
      // אחרת, נבדוק אם product_type בתוך insuranceFundsTypes
      const isValidType = adjusted_product_type != null
        ? MimshakService.insuranceFundsTypesByAdjustedProductType.includes(adjusted_product_type)
        : MimshakService.insuranceFundsTypes.includes(product_type);

      if (!isValidType) {
        return false;
      }

      return true;
    })
  );
}

/**
* Filters Mimshaks based on a specific Client ID and returns a new list of Mimshaks.
* @param mimshaks - List of Mimshak objects.
* @param clientId - The Client ID to filter by.
* @returns Filtered list of Mimshaks with Products matching the Client ID.
*/
static filterMimshaksByClientId(mimshaks: Mimshak[], clientId: string): Mimshak[] {
const result: Mimshak[] = [];

mimshaks.forEach((mimshak) => {
  // סינון המוצרים של הלקוח
  const filteredProducts = mimshak.products.filter(
    (product) => product.client?.client_id === clientId
  );

  // אם נמצאו מוצרים תואמים, צור אובייקט Mimshak חדש
  if (filteredProducts.length > 0) {
    const newMimshak: Mimshak = {
      ...mimshak, // שאר השדות מתוך ה-Mimshak המקורי
      products: filteredProducts, // המוצרים התואמים
    };

    result.push(newMimshak);
  }
});

return result;
}
//#endregion פילטרים
//#region פונקציות לחישוב סכום
static calculatePolicyTotalsByType(  policy: PolicyOrAccount,  balanceType: LiquidityPeriodBalanceType): number {
  return (policy.accumulated_savings ?? [])
    .filter((path) => path.savings_type_code === balanceType)
    .reduce((total, path) => total + path.total_savings, 0);
}
static calculatePolicyTotalsByTypeWithNot(  policy: PolicyOrAccount,  notBalanceType: LiquidityPeriodBalanceType): number {
  return (policy.accumulated_savings ?? [])
    .filter((path) => path.savings_type_code !== notBalanceType)
    .reduce((total, path) => total + path.total_savings, 0);
}
 /**
 * Calculates the total accumulated amount for a policy.
 * @param policy - The policy to calculate the total for.
 * @returns Total accumulated amount for the policy.
 */
 static calculatePolicyTotalAmountPension(policy: PolicyOrAccount): number {
  return this.calculatePolicyTotalsByTypeWithNot(policy,LiquidityPeriodBalanceType.CAPITAL);
}
static calculatePolicyTotalAmountCapital(policy: PolicyOrAccount): number {
  return this.calculatePolicyTotalsByType(policy,LiquidityPeriodBalanceType.CAPITAL);
}

/**
 * Calculates the total accumulated amount for a list of policies or accounts using an existing utility function.
 * @param policies - List of PolicyOrAccount.
 * @returns Total accumulated amount for the policies.
 */
static calculateTotalPolicySumCapital(policies: PolicyOrAccount[]): number {
  return policies.reduce(
    (total, policy) => total + this.calculatePolicyTotalsByType(policy,LiquidityPeriodBalanceType.CAPITAL),
    0
  );
}

static calculateTotalPolicySumPension(policies: PolicyOrAccount[]): number {
  return policies.reduce(
    (total, policy) => total + this.calculatePolicyTotalsByTypeWithNot(policy,LiquidityPeriodBalanceType.CAPITAL),
    0
  );
}
 

  /**
 * Calculates the total accumulated amount for a single product.
 * @param product - The product to calculate the total for.
 * @returns Total accumulated amount for the product.
 */
static calculateProductTotalAmount(product: Product,balanceType: LiquidityPeriodBalanceType): number {
  return product.policies_or_accounts.reduce(
    (policyTotal, policy) =>
      policyTotal + this.calculatePolicyTotalsByType(policy,balanceType),
    0
  );
}
static calculateProductTotalAmountCapital(product: Product): number {
  return product.policies_or_accounts.reduce(
    (policyTotal, policy) =>
      policyTotal + this.calculatePolicyTotalsByType(policy,LiquidityPeriodBalanceType.CAPITAL),
    0
  );
}
static calculateProductTotalAmountPension(product: Product): number {
  return product.policies_or_accounts.reduce(
    (policyTotal, policy) =>
      policyTotal + this.calculatePolicyTotalsByTypeWithNot(policy,LiquidityPeriodBalanceType.CAPITAL),
    0
  );
}
 /**
 * Calculates the total accumulated amount for a list of products.
 * @param products - List of products.
 * @returns Total accumulated amount.
 */
 static calculateTotalFundSum(products: Product[],balanceType: LiquidityPeriodBalanceType): number {
  return products.reduce(
    (total, product) => total + MimshakService.calculateProductTotalAmount(product,balanceType),
    0
  );
}
static calculateTotalFundSumCapital(products: Product[]): number {
  return products.reduce(
    (total, product) => total + MimshakService.calculateProductTotalAmount(product,LiquidityPeriodBalanceType.CAPITAL),
    0
  );
}
static calculateTotalFundSumPension(products: Product[]): number {
  return products.reduce(
    (total, product) => total + MimshakService.calculateProductTotalAmountPension(product),
    0
  );
}
 // === 🟢 מחשב סכום כולל בלי סינון לפי סוג ===
 static calculateTotals(policies: PolicyOrAccount[]): number {
  return policies.reduce(
    (total, policy) => total + this.calculatePolicyTotals(policy),
    0
  );
}
static calculateTotalsFromProducts(products: Product[]): number {
  return products.reduce((sum, product) => {
    return sum + this.calculateTotals(product.policies_or_accounts);
  }, 0);
}
// === 🟢 מחשב סכום כולל מתוך פוליסה ===
static calculatePolicyTotals(policy: PolicyOrAccount): number {
  return (policy.accumulated_savings ?? [])
    .reduce((total, path) => total + path.total_savings, 0);
}
/**
 * Calculates the total accumulated amount for a list of policies or accounts using an existing utility function.
 * @param policies - List of PolicyOrAccount.
 * @returns Total accumulated amount for the policies.
 */
static calculateTotalsByType(policies: PolicyOrAccount[],balanceType: LiquidityPeriodBalanceType): number {
  return policies.reduce(
    (total, policy) => total + this.calculatePolicyTotalsByType(policy,balanceType),
    0
  );
}
static calculatePensionPoliciesTotals(policies: PolicyOrAccount[]): number {
  return policies.reduce(
    (total, policy) => total + this.calculatePolicyTotalsByTypeWithNot(policy,LiquidityPeriodBalanceType.CAPITAL),
    0
  );
}

static calculateTotalsByTypeWithNot(policies: PolicyOrAccount[],notbalanceType: LiquidityPeriodBalanceType): number {
  return policies.reduce(
    (total, policy) => total + this.calculatePolicyTotalsByTypeWithNot(policy,notbalanceType),
    0
  );
}

// Returns the fraction of a policy's total savings that belongs to a given balance type (0–1).
static getPolicyBalanceRatio(policy: PolicyOrAccount, balanceType: LiquidityPeriodBalanceType): number {
  const typeAmount = this.calculatePolicyTotalsByType(policy, balanceType);
  const total = (policy.accumulated_savings ?? []).reduce((s, a) => s + a.total_savings, 0);
  return total > 0 ? typeAmount / total : 0;
}

static getPolicyTotalAccumulatedSavings(policy: PolicyOrAccount): number {
  return (policy.accumulated_savings ?? []).reduce((s, a) => s + a.total_savings, 0);
}

// Returns the CAPITAL fraction of a policy that becomes liquid only after minYearsAhead years from today.
// Policies with no liquidity date, or with a liquidity date within the threshold, return 0.
static getFuturePolicyRatio(policy: PolicyOrAccount, minYearsAhead: number): number {
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() + minYearsAhead);
  const capitalAmount = this.calculatePolicyTotalsByType(policy, LiquidityPeriodBalanceType.CAPITAL);
  if (capitalAmount <= 0) return 0;
  const liquidityDate = policy.liquidity_summaries?.find(s => s.liquidity_date)?.liquidity_date;
  const isFutureCapital = liquidityDate ? new Date(liquidityDate) > cutoff : false;
  if (!isFutureCapital) return 0;
  const total = this.getPolicyTotalAccumulatedSavings(policy);
  return total > 0 ? capitalAmount / total : 0;
}

// Combined ratio for the "כספים עתידיים" view: NON_PAYING_PENSION + CAPITAL locked beyond capitalMinYearsAhead years.
static getFutureAndNonPayingPensionRatio(policy: PolicyOrAccount, capitalMinYearsAhead: number): number {
  const nonPayingRatio = this.getPolicyBalanceRatio(policy, LiquidityPeriodBalanceType.NON_PAYING_PENSION);
  const futureCapitalRatio = this.getFuturePolicyRatio(policy, capitalMinYearsAhead);
  return Math.min(1, nonPayingRatio + futureCapitalRatio);
}

//#endregion חישובי סכום
 //#region fees
 static getLastFeesByExpenseType(
  fees: ManagementFeeDetails[], 
  expenseType: PensionFeeExpenseType
): ManagementFeeDetails | null {
  const filteredFees = fees.filter(fee => fee.expense_type === expenseType);
  
  if (filteredFees.length === 0) {
    return null;
  }
  
  return filteredFees.reduce((latest, current) => {
    return new Date(latest.last_rate_update_date || '1970-01-01') > new Date(current.last_rate_update_date || '1970-01-01')
      ? latest
      : current;
  });
}
static evaluateFeeBenefitExpiration(fee: ManagementFeeDetails | null): "ALERT" | "WARNING" | null {
  if (!fee || !fee.benefit_expiration_date) {
    return null;
  }
  
  const expirationDate = new Date(fee.benefit_expiration_date);
  const currentDate = new Date();
  const oneMonthFromNow = new Date();
  const sixMonthsFromNow = new Date();
  oneMonthFromNow.setMonth(currentDate.getMonth() + 1);
  sixMonthsFromNow.setMonth(currentDate.getMonth() + 6);
  
  if (expirationDate <= oneMonthFromNow) {
    return "ALERT";
  } else if (expirationDate <= sixMonthsFromNow) {
    return "WARNING";
  }
  
  return null;
}
static isUpdateOlderThanThreeYears(dateString?: string): boolean {
  if (!dateString) return false;
  const lastUpdate = new Date(dateString);
  const threeYearsAgo = new Date();
  threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
  return lastUpdate < threeYearsAgo;
}
static getProductsMinMaxFeesByExpenseType(
  products: Product[],
  expenseType: PensionFeeExpenseType
): { min: number | null; max: number | null } {
  const allPolicies: PolicyOrAccount[] = products.flatMap(
    (product) => product.policies_or_accounts || []
  );

  return MimshakService.getMinMaxFeesByExpenseType(allPolicies, expenseType);
}
static getMinMaxFeesByExpenseType(
  policiesOrAccounts: PolicyOrAccount[],
  expenseType: PensionFeeExpenseType
): { min: number | null; max: number | null } {
  if (!policiesOrAccounts || policiesOrAccounts.length === 0) {
    return { min: null, max: null };
  }

  // שליפת ה-Fee האחרון מכל פוליסה לפי ה-ExpenseType
  const lastFees = policiesOrAccounts
    .map(policyOrAccount =>
      MimshakService.getLastFeesByExpenseType(policyOrAccount.management_fee_details || [], expenseType)
    )
    .filter(fee => fee !== null && fee.management_fee_rate !== undefined) as ManagementFeeDetails[];

  if (lastFees.length === 0) {
    return { min: null, max: null };
  }

  // חישוב מינימום ומקסימום של management_fee_rate
  const minFee = Math.min(...lastFees.map(fee => fee.management_fee_rate!));
  const maxFee = Math.max(...lastFees.map(fee => fee.management_fee_rate!));

  return { min: minFee, max: maxFee };
}


//endregion fees

//#region others 

/**
 * Extracts all liquidity summaries from a list of policies or accounts.
 * @param policies - Array of policies or accounts.
 * @returns Flat list of liquidity summaries.
 */
static getAllLiquiditySummaries(policies: PolicyOrAccount[]): LiquiditySummary[] {
  return policies.flatMap((policy) => policy.liquidity_summaries || []);
}

static getPastLiquidityAmountSum(policies: PolicyOrAccount[], referencePeriod?: string | Date): number {
  const now = referencePeriod ? new Date(referencePeriod) : new Date();

  return policies
    .flatMap((policy) => policy.liquidity_summaries || [])
    .filter((summary) => {
      if (!summary.liquidity_date) return false;
      const liquidityDate = new Date(summary.liquidity_date); // תומך בפורמט ISO
      return liquidityDate < now;
    })
    .reduce((sum, summary) => sum + summary.liquidity_amount, 0);
}

static getFutureLiquiditySummaries(policies: PolicyOrAccount[], referencePeriod?: string | Date): LiquiditySummary[] {
  const now = referencePeriod ? new Date(referencePeriod) : new Date();

  return policies
    .flatMap((policy) => policy.liquidity_summaries || [])
    .filter((summary) => {
      if (!summary.liquidity_date) return false;
      const liquidityDate = new Date(summary.liquidity_date); // תומך בפורמט ISO
      return liquidityDate > now;
    });
}



static getDistinctUpdateDates(policies: PolicyOrAccount[]): string[] {
  // Extract unique update_date values
  const uniqueDates = Array.from(
    new Set(policies.map((policy) => policy.update_date))
  );
  return uniqueDates;
}

static getAllLoans(policies: PolicyOrAccount[]): LoanDetails[] {
  return policies.flatMap((policy) => policy.loan_details || []);
}

static getAllLoansFromMimshak(mimshak?: Mimshak): LoanDetails[] {
  if (!mimshak?.products) {
    return [];
  }
  const allPolicies = mimshak.products.flatMap((product) => product.policies_or_accounts || []);
  return this.getAllLoans(allPolicies);
}

static getAllFundActions(policies: PolicyOrAccount[]): FundAction[] {
  return policies.flatMap((policy) => policy.fund_actions || []);
}
static groupFundActionsByActionType(policies: PolicyOrAccount[]): Record<number, number> {
  // שליפת כל ה-FundActions מכל הפוליסות
  const allFundActions = policies.flatMap((policy) => policy.fund_actions || []);

  // יצירת קבוצה לפי action_type_code וחישוב סכום withdrawal_amount לכל קוד פעולה
  const groupedActions: Record<number, number> = {};

  allFundActions.forEach((action) => {
    if (!groupedActions[action.action_type_code]) {
      groupedActions[action.action_type_code] = 0;
    }
    groupedActions[action.action_type_code] += action.withdrawal_amount;
  });

  return groupedActions;
}
static groupWithdrawalsByMonth(
  policies: PolicyOrAccount[]
): { month: string; total_withdrawal_amount: number }[] {
  // שליפת כל ה-FundActions מכל הפוליסות
  const allFundActions: FundAction[] = policies.flatMap((policy) => policy.fund_actions || []);

  // סינון הפעולות לסוג WITHDRAWAL בלבד
  const withdrawalActions = allFundActions.filter(
    (action) => action.action_type_code === ActionTypeCode.WITHDRAWAL
  );

  // אובייקט לקיבוץ לפי חודש
  const groupedByMonth: Record<string, number> = {};

  withdrawalActions.forEach((action) => {
    const executionDate = action.execution_date;

    if (executionDate) {
      // שליפת השנה והחודש מהתאריך
      const [year, month] = executionDate.split("-");

      // יצירת מפתח בפורמט YYYY-MM
      const monthKey = `${year}-${month}`;

      // חישוב סכום המשיכות לחודש
      if (!groupedByMonth[monthKey]) {
        groupedByMonth[monthKey] = 0;
      }

      groupedByMonth[monthKey] += action.withdrawal_amount;
    }
  });

  // המרת Record למערך JSON
  return Object.entries(groupedByMonth).map(([month, total]) => ({
    month,
    total_withdrawal_amount: total,
  }));
}

static calculateTotalLoanAmount(policies: PolicyOrAccount[]): number {
  const loans = this.getAllLoans(policies);
  return loans.reduce((sum, loan) => sum + (loan.loan_amount || 0), 0);
}
static getDistinctLoansWithPolicy(products: Product[]): LoanWithPolicy[] {
    const seenKeys = new Set<string>();
    const distinctLoans: LoanWithPolicy[] = [];

    products.forEach(product => {
      const handler = product.details.handler_code;

      product.policies_or_accounts.forEach(policy => {
        (policy.loan_details || []).forEach(loan => {
          const key = `${handler}-${loan.loan_serial_number}`;
          if (!seenKeys.has(key)) {
            seenKeys.add(key);
            distinctLoans.push({ loan, policy });
          }
        });
      });
    });

    return distinctLoans;
  }
static getDeduplicatedLoansByProduct(products: Product[]): Record<string, LoanWithPolicy[]> {
  const distinctLoans = MimshakService.getDistinctLoansWithPolicy(products);
  const result: Record<string, LoanWithPolicy[]> = {};

  products.forEach((_, idx) => {
    result[`p-${idx}`] = [];
  });

  distinctLoans.forEach(({ loan, policy }) => {
    // מוצא את האינדקס של המוצר שבו נמצאת הפוליסה הזאת
    const productIndex = products.findIndex(product =>
      product.policies_or_accounts.includes(policy)
    );

    if (productIndex !== -1) {
      const key = `p-${productIndex}`;
      result[key].push({ loan, policy });
    }
  });

  return result;
}

/**
 * Extracts and deduplicates loans from MimshakData array.
 * Loans are deduplicated by handler_code + loan_serial_number.
 * @param mimshakDataList - Array of MimshakData objects
 * @returns Array of deduplicated LoanFromMimshakData objects
 */
static getDeduplicatedLoansFromMimshakData(mimshakDataList: MimshakData[]): LoanFromMimshakData[] {
  const seenKeys = new Set<string>();
  const distinctLoans: LoanFromMimshakData[] = [];

  mimshakDataList.forEach((mimshakData) => {
    if (!mimshakData.data?.products) return;

    mimshakData.data.products.forEach((product) => {
      const handlerCode = product.details?.handler_code || mimshakData.handler_code;

      product.policies_or_accounts.forEach((policy) => {
        (policy.loan_details || []).forEach((loan) => {
          const key = `${handlerCode}-${loan.loan_serial_number}`;
          if (!seenKeys.has(key)) {
            seenKeys.add(key);
            distinctLoans.push({
              loan,
              handlerCode,
              policyNumber: policy.policy_number,
              productType: product.details?.product_type || mimshakData.product_type,
            });
          }
        });
      });
    });
  });

  return distinctLoans;
}

static calculateTotalLoanBalance(policies: PolicyOrAccount[]): number {
  const loans = this.getAllLoans(policies);
  return loans.reduce((sum, loan) => sum + (loan.loan_balance || 0), 0);
}

static calculateAverageLoanInterestRate(policies: PolicyOrAccount[]): number {
  const loans = this.getAllLoans(policies);
  if (loans.length === 0) {
    return 0;
  }
  const totalInterest = loans.reduce((sum, loan) => sum + (loan.interest_rate || 0), 0);
  return totalInterest / loans.length;
}


/**
 * Extracts all path codes from a list of policies or accounts.
 * @param policiesOrAccounts - Array of policies or accounts.
 * @returns Array of unique path codes.
 */
static extractPathCodes(policiesOrAccounts: PolicyOrAccount[]): number[] {
  const pathCodes = policiesOrAccounts.flatMap((policy) =>
    policy.investment_paths.map((path) => path.path_code)
  );

  // הסרת כפילויות (אם יש)
  return Array.from(new Set(pathCodes));
}
static extractPathCodesFromProducts = (products: Product[]): number[] => {
  const codes: number[] = [];
  products?.forEach((p) =>
    p.policies_or_accounts?.forEach((policy) =>
      policy.investment_paths?.forEach((path) => {
        if (typeof path?.path_code === "number") {
          codes.push(path.path_code);
        }
      })
    )
  );
  return Array.from(new Set(codes));
};

static findInvestmentPathByPathCode = (
  products: Product[],
  policyNumber: string,
  pathCode: number
): InvestmentPathDetail | undefined => {
  return products
    .flatMap((p) => p.policies_or_accounts)
    .filter((p) => p.policy_number === policyNumber)
    .flatMap((p) => p.investment_paths || [])
    .find((path) => path.path_code === pathCode);
};


  /**
   * Aggregates liquidity details grouped by matched deposit cap.
   * @param policies - List of policies or accounts.
   * @returns A map of matched deposit caps to their total balance amounts.
   */
  static groupLiquidityDetailsByMatchedDepositCap(
    policies: PolicyOrAccount[]
  ): Record<number, number> {
    return policies.flatMap((policy) => policy.liquidity_details || []).reduce(
      (acc, detail) => {
        const key = detail.matched_deposit_cap;
        if (!acc[key]) {
          acc[key] = 0;
        }
        acc[key] += detail.balance_amount || 0;
        return acc;
      },
      {} as Record<number, number>
    );
  }
static mergeDuplicateInvestmentPathsByPathCode(
  investmentPaths: InvestmentPathDetail[]
): InvestmentPathDetail[] {
  const grouped = investmentPaths.reduce<Record<string, InvestmentPathDetail>>(
    (acc, path) => {
      const code = path.path_code;
      if (!acc[code]) {
        acc[code] = { ...path }; // העתק ראשון
      } else {
        acc[code].accumulated_amount =
          (acc[code].accumulated_amount ?? 0) +
          (path.accumulated_amount ?? 0);
        const sumPercentage =
          (acc[code].deposit_percentage ?? 0) +
          (path.deposit_percentage ?? 0);
        acc[code].deposit_percentage = Math.round(sumPercentage * 100) / 100;
      }
      return acc;
    },
    {}
  );

  return Object.values(grouped);
}

  /*
     * איחוד מסלולים זהים למעט accumulated_amount
     * @param investmentPaths - מערך של מסלולי השקעה
     * @returns מערך מצומצם עם סכום נצבר מאוחד למסלולים זהים
     */
  static mergeDuplicateInvestmentPaths(
    investmentPaths: InvestmentPathDetail[]
  ): InvestmentPathDetail[] {
    const grouped = _.groupBy(investmentPaths, (path) => path.path_code);

    const mergedPaths: InvestmentPathDetail[] = [];

    for (const groupKey in grouped) {
      const group = grouped[groupKey];
      const base = group[0];

      const totalAccumulatedAmount = _.sumBy(group, 'accumulated_amount');
      const totalDepositPercentage = _.sumBy(group, 'deposit_percentage');

      mergedPaths.push({
        ...base,
        accumulated_amount: totalAccumulatedAmount,
        deposit_percentage: Math.round(totalDepositPercentage * 100) / 100
      });
    }

    return mergedPaths;
  }
  static getLatestUpdateDate(policies: PolicyOrAccount[]): string | null {
    if (!policies || policies.length === 0) {
      return null; // מחזירים null אם אין פוליסות
    }
  
    const latestDate = policies.reduce((latest, policy) => 
      policy.update_date && policy.update_date > latest ? policy.update_date : latest,
      "0000-00-00"
    );
  
    return latestDate === "0000-00-00" ? null : latestDate; // אם לא נמצא תאריך תקף, מחזירים null
  }
  
  
  static getLatestUpdateDateFromProducts(products: Product[]): string {
    if (!products || products.length === 0) {
      return "0000-00-00"; // ברירת מחדל אם אין מוצרים
    }
  
    // שליפת כל ה- Policies או Accounts מהמוצרים
    const policies: PolicyOrAccount[] = this.getAllPoliciesOrAccounts(products);
  
    // שימוש בפונקציה שכבר כתבנו כדי לקבל את התאריך העדכני ביותר
    return this.getLatestUpdateDate(policies);
  }
  
/**
 * Groups a list of deposits by their deposit date.
 *
 * This function takes an array of deposit details and organizes them into a record,
 * where each key is a deposit date (as a string), and the value is an object containing:
 * - `total`: The sum of all deposit amounts for that date.
 * - `details`: An array of all deposit details for that date.
 *
 * @param {DepositDetail[]} deposits - An array of deposit details to be grouped.
 * @returns {Record<string, { total: number; details: DepositDetail[] }>}
 *          A record where each key is a deposit date, and the value contains:
 *          - `total`: Total deposit amount for that date.
 *          - `details`: Array of all deposit details for that date.
 *
 * @example
 * const deposits = [
 *   { deposit_date: '2023-12-25', deposit_amount_paid: 100 },
 *   { deposit_date: '2023-12-25', deposit_amount_paid: 200 },
 *   { deposit_date: '2023-12-26', deposit_amount_paid: 150 },
 * ];
 *
 * const result = groupDepositsByDate(deposits);
 * console.log(result);
 * // Output:
 * // {
 * //   '2023-12-25': { total: 300, details: [{...}, {...}] },
 * //   '2023-12-26': { total: 150, details: [{...}] }
 * // }
 */
  static groupDepositsByDate = (
    deposits: DepositDetail[]
  ): Record<string, { total: number; details: DepositDetail[] }> => {
    return deposits.reduce(
      (
        acc: Record<string, { total: number; details: DepositDetail[] }>,
        deposit
      ) => {
        const { deposit_date, deposit_amount_paid } = deposit;
        if (!acc[deposit_date]) {
          acc[deposit_date] = { total: 0, details: [] };
        }
        acc[deposit_date].total += deposit_amount_paid;
        acc[deposit_date].details.push(deposit);
        return acc;
      },
      {}
    );
  };
  static calculateTotalDeposits = (deposits: DepositDetail[]): number => {
    return deposits.reduce((sum, deposit) => sum + deposit.deposit_amount_paid, 0);
  };
  static calculateTotalDepositsForLast12Months(policies: PolicyOrAccount[], isoDate: string): number {
    // ממירים את תאריך ה-ISO לאובייקט Date
    const referenceDate = new Date(isoDate);
    if (isNaN(referenceDate.getTime())) {
      throw new Error("Invalid ISO date provided");
    }
  
    // מחשבים את התאריך שהיה לפני 12 חודשים
    const twelveMonthsAgo = new Date(referenceDate);
    twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);
  
    // שליפת כל ההפקדות מכל הפוליסות
    const allDeposits: DepositDetail[] = policies.flatMap((policy) => policy.deposits);
  
    // סינון הפקדות שבוצעו ב-12 החודשים האחרונים
    const depositsLast12Months = allDeposits.filter((deposit) => {
      const depositDate = new Date(deposit.deposit_date); // assuming `deposit_date` is an ISO string
      return depositDate >= twelveMonthsAgo && depositDate <= referenceDate;
    });
  
    // חישוב הסכום הכולל של ההפקדות בטווח הזמן שנבחר
    return this.calculateTotalDeposits(depositsLast12Months);
  }
  static getTotalDepositsLast12Months(products: Product[], isoDate: string): number {
    if (!products || products.length === 0) {
      return 0;
    }
  
    // שליפת כל ה- Policies או Accounts מהמוצרים
    const policies: PolicyOrAccount[] = this.getAllPoliciesOrAccounts(products);
  
    // קריאה לפונקציה החדשה לביצוע החישוב בפועל
    return this.calculateTotalDepositsForLast12Months(policies, isoDate);
  }
  

  static getTotalDepositsForYear(products: Product[], year: number = new Date().getFullYear()): number {
    if (!products || products.length === 0) {
      return 0;
    }

    // שליפת כל ה- Policies או Accounts מהמוצרים
    const policies: PolicyOrAccount[] = this.getAllPoliciesOrAccounts(products);

    // קיבוץ ההפקדות לפי שנה
    const depositsByPeriod = this.groupAllDepositsByPaymentPeriod(policies);

    // מציאת ההפקדות לשנה המבוקשת
    return depositsByPeriod.find(d => d.period === year)?.totalSum || 0;
  }


  static calculateAverageDeposit = (deposits: DepositDetail[]): number => {
    if (deposits.length === 0) return 0;
    const totalDepositsSum = MimshakService.calculateTotalDeposits(deposits);
    return totalDepositsSum / deposits.length;
  };
  static getDepositsForLast12Months(
    policies: PolicyOrAccount[],
    isoDate: string,
    dateMode: 'salary_month' | 'payment_period' | 'deposit_date' = 'payment_period'
  ): DepositDetail[] {
    // ממירים את תאריך ה-ISO לאובייקט Date
  const referenceDate = new Date(isoDate);
  if (isNaN(referenceDate.getTime())) {
    throw new Error("Invalid ISO date provided");
  }

  // Calculate 12 months ago
  const twelveMonthsAgo = new Date(referenceDate);
  twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);

  // Calculate 12 months ago in YYYYMM format (for salary_month and payment_period)
  const referenceYear = referenceDate.getFullYear();
  const referenceMonth = referenceDate.getMonth() + 1; // 1-12
  const referencePeriod = referenceYear * 100 + referenceMonth; // e.g., 202505

  const startYear = twelveMonthsAgo.getFullYear();
  const startMonth = twelveMonthsAgo.getMonth() + 1; // 1-12
  const startPeriod = startYear * 100 + startMonth; // e.g., 202405

  // שליפת כל ההפקדות מכל הפוליסות
  const allDeposits: DepositDetail[] = policies.flatMap((policy) => policy.deposits);

  // סינון ההפקדות לפי 12 חודשים אחרונים
  const res = allDeposits.filter((deposit) => {
    if (dateMode === 'deposit_date') {
      // Filter by deposit_date
      if (!deposit.deposit_date) return false;
      const depositDate = new Date(deposit.deposit_date);
      return depositDate >= twelveMonthsAgo && depositDate <= referenceDate;
    } else if (dateMode === 'salary_month' && deposit.salary_month && deposit.salary_month.trim() !== '') {
      // Filter by salary_month
      const salaryMonthValue = parseInt(deposit.salary_month);
      return salaryMonthValue >= startPeriod && salaryMonthValue <= referencePeriod;
    } else {
      // Filter by payment_period
      const periodValue = deposit.payment_period;
      return periodValue >= startPeriod && periodValue <= referencePeriod;
    }
  });
  return res;
}
  static getDepositPeriodsCountForLast12Months(policies: PolicyOrAccount[], isoDate: string): number {
    // ממירים את תאריך ה-ISO לאובייקט Date
    const referenceDate = new Date(isoDate);
    if (isNaN(referenceDate.getTime())) {
      throw new Error("Invalid ISO date provided");
    }
  
    // מחשבים את התאריך שהיה לפני 12 חודשים
    const twelveMonthsAgo = new Date(referenceDate);
    twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);
  
    // שליפת כל ההפקדות מכל הפוליסות
    const allDeposits: DepositDetail[] = policies.flatMap((policy) => policy.deposits);
  
    // סינון הפקדות מה-12 חודשים האחרונים
    const depositsLast12Months = allDeposits.filter((deposit) => {
      const depositDate = new Date(deposit.deposit_date); // assuming `deposit_date` is an ISO string
      return depositDate >= twelveMonthsAgo && depositDate <= referenceDate;
    });
  
    // יצירת אוסף ייחודי של כל תקופות ההפקדה
    const uniquePeriods = new Set(depositsLast12Months.map((deposit) => deposit.payment_period));
  
    // מחזירים את מספר התקופות השונות
    return uniquePeriods.size;
  }
  static getDepositPeriodsCountForLast12MonthsFromProducts(products: Product[], isoDate: string): number {
    if (!products || products.length === 0) {
      return 0;
    }
  
    // שליפת כל הפוליסות מהמוצרים
    const policies: PolicyOrAccount[] = this.getAllPoliciesOrAccounts(products);
  
    // שימוש בפונקציה שכבר כתבנו
    return this.getDepositPeriodsCountForLast12Months(policies, isoDate);
  }
  static groupDepositsByPaymentPeriod(
    deposits: DepositDetail[]
  ): { period: number; totalSum: number; depositDetails: DepositDetail[] }[] {
    const groupedMap: Map<
      number,
      { period: number; totalSum: number; depositDetails: DepositDetail[] }
    > = new Map();
  
    // Iterate through the deposits and group by payment_period
    deposits.forEach((deposit) => {
      const period = deposit.payment_period;
  
      // Check if the period already exists in the map
      if (groupedMap.has(period)) {
        const group = groupedMap.get(period)!;
        group.totalSum += deposit.deposit_amount_paid;
        group.depositDetails.push(deposit);
      } else {
        // Create a new group for this period
        groupedMap.set(period, {
          period: period,
          totalSum: deposit.deposit_amount_paid,
          depositDetails: [deposit],
        });
      }
    });
  
    // Convert the map to an array and sort by period in ascending order
    return Array.from(groupedMap.values()).sort((a, b) => a.period - b.period);
  }
  

  static groupAllDepositsByYear(
    policies: PolicyOrAccount[],
    preferPaymentPeriod: boolean = false
  ): { year: number; totalSum: number; depositDetails: DepositDetail[] }[] {
    // שליפת כל ההפקדות מכל הפוליסות
    const allDeposits: DepositDetail[] = policies.flatMap((policy) => policy.deposits);

    // מיפוי לפי שנה
    const groupedMap: Map<number, { year: number; totalSum: number; depositDetails: DepositDetail[] }> = new Map();

    allDeposits.forEach((deposit) => {
      // Determine which date field to use based on preferPaymentPeriod flag
      let dateValue: number;
      if (preferPaymentPeriod) {
        // For self-employed: prefer payment_period, fallback to salary_month
        if (deposit.payment_period) {
          dateValue = deposit.payment_period; // e.g., 202501
        } else if (deposit.salary_month && deposit.salary_month.trim() !== '') {
          dateValue = parseInt(deposit.salary_month); // e.g., 202501
        } else {
          return; // Skip deposits without valid date information
        }
      } else {
        // For employees: prefer salary_month, fallback to payment_period
        if (deposit.salary_month && deposit.salary_month.trim() !== '') {
          dateValue = parseInt(deposit.salary_month); // e.g., 202501
        } else if (deposit.payment_period) {
          dateValue = deposit.payment_period; // e.g., 202501
        } else {
          return; // Skip deposits without valid date information
        }
      }

      // Extract year from YYYYMM format
      const year = Math.floor(dateValue / 100); // e.g., 202501 -> 2025

      if (groupedMap.has(year)) {
        const group = groupedMap.get(year)!;
        group.totalSum += deposit.deposit_amount_paid;
        group.depositDetails.push(deposit);
      } else {
        groupedMap.set(year, {
          year: year,
          totalSum: deposit.deposit_amount_paid,
          depositDetails: [deposit],
        });
      }
    });

    return Array.from(groupedMap.values()).sort((a, b) => a.year - b.year);
  }
  
  //returns list of
// period : 202412
// totalSum : 63500
//depositDetails:[]
  static groupAllDepositsByPaymentPeriod(
    policies: PolicyOrAccount[]
  ): { period: number; totalSum: number; depositDetails: DepositDetail[] }[] {
    // Collect all deposits from all policies
    const allDeposits: DepositDetail[] = policies.flatMap((policy) => policy.deposits);
  
    // Use the existing function to group the deposits
    return MimshakService.groupDepositsByPaymentPeriod(allDeposits);
  }
  
  static getLatestDeposit(
    policies: PolicyOrAccount[]
  ): DepositDetail | null {
    // קיבוץ כל ההפקדות לפי תקופות
    const groupedDeposits = MimshakService.groupAllDepositsByPaymentPeriod(policies);

    if (groupedDeposits.length === 0) {
      return null; // אם אין הפקדות, מחזירים null
    }

    // שליפת הקבוצה עם התקופה הגבוהה ביותר
    const latestDepositGroup = groupedDeposits[groupedDeposits.length - 1];

    // בדיקת האם יש נתונים בקבוצה
    if (latestDepositGroup.depositDetails.length === 0) {
      return null;
    }

    // שליפת ההפקדה הראשונה מתוך הקבוצה האחרונה
    return latestDepositGroup.depositDetails[0];
  }

  /**
   * Gets all deposits from the latest payment period
   * @param policies - List of policies or accounts
   * @returns Array of deposit details from the latest payment period, or empty array if no deposits exist
   */
  static getLatestDepositDetails(
    policies: PolicyOrAccount[]
  ): DepositDetail[] {
    // קיבוץ כל ההפקדות לפי תקופות
    const groupedDeposits = MimshakService.groupAllDepositsByPaymentPeriod(policies);

    if (groupedDeposits.length === 0) {
      return []; // אם אין הפקדות, מחזירים מערך רק
    }

    // שליפת הקבוצה עם התקופה הגבוהה ביותר
    const latestDepositGroup = groupedDeposits[groupedDeposits.length - 1];

    return latestDepositGroup.depositDetails || [];
  }

  /**
   * Gets the maximum salary base from the latest payment period
   * @param policies - List of policies or accounts
   * @returns Maximum salary base from latest deposits, or null if no deposits exist
   */
  static getMaxSalaryBaseFromLatestDeposits(
    policies: PolicyOrAccount[]
  ): number | null {
    const latestDeposits = MimshakService.getLatestDepositDetails(policies);

    if (latestDeposits.length === 0) {
      return null;
    }

    const maxSalaryBase = Math.max(...latestDeposits.map(d => d.salary_base || 0));
    return maxSalaryBase > 0 ? maxSalaryBase : null;
  }

  /**
   * Calculates the determining salary (שכר קובע) from the latest deposits
   * This is typically calculated as: salary_base * 1.085 (including additional benefits)
   * @param policies - List of policies or accounts
   * @returns Determining salary, or null if no deposits exist
   */
  static getDeterminingSalaryFromLatestDeposits(
    policies: PolicyOrAccount[]
  ): number | null {
    const maxSalaryBase = MimshakService.getMaxSalaryBaseFromLatestDeposits(policies);

    if (maxSalaryBase === null) {
      return null;
    }

    // שכר קובע = בסיס שכר + 8.5% (הפרשה ממעביד)
    return maxSalaryBase * 1.085;
  }
 /**
   * Aggregates liquidity details grouped by balance component.
   * @param liquidityDetails - List of liquidity details.
   * @returns A map of balance components to their total balance amounts.
   */
 static groupLiquidityDetailsByBalanceComponent(
  liquidityDetails: LiquidityDetails[]
): Record<string, number> {
  return liquidityDetails.reduce((acc, detail) => {
    const key = detail.balance_component || "Unknown";
    if (!acc[key]) {
      acc[key] = 0;
    }
    acc[key] += detail.balance_amount || 0;
    return acc;
  }, {} as Record<string, number>);
}

/**
 * Aggregates all liquidity details from policies and groups them by matched deposit cap.
 * @param policies - List of policies or accounts.
 * @returns A map of matched deposit caps to their total balance amounts.
 */
static groupAllLiquidityDetailsByMatchedDepositCap(
  policies: PolicyOrAccount[]
): Record<number, number> {
  const allLiquidityDetails = policies.flatMap((policy) => policy.liquidity_details || []);

  const groupedByMatchedDepositCap = allLiquidityDetails.reduce(
    (acc, detail) => {
      const key = detail.matched_deposit_cap;
      if (!acc[key]) {
        acc[key] = 0;
      }
      acc[key] += detail.balance_amount || 0;
      return acc;
    },
    {} as Record<number, number>
  );

  return groupedByMatchedDepositCap;
}

/**
 * Calculates total education fund amounts and tax-exempt amounts.
 * Returns the total balance and the tax-exempt portion (up to cap).
 * @param products - List of products.
 * @returns Object with totalAmount and taxExemptAmount
 */
static getEducationFundTaxInfo(
  products: Product[]
): { totalAmount: number; taxExemptAmount: number } {
  // Filter only education fund products
  const educationFundProducts = MimshakService.filterProductsByType2(products, ProductType.EDUCATION_FUND);

  // Get all policies from education fund products
  const educationFundPolicies = MimshakService.getAllPoliciesOrAccounts(educationFundProducts);

  // Calculate total accumulated amount using existing utility function
  const totalAmount = MimshakService.calculateTotalFundSumCapital(educationFundProducts);

  // Get all liquidity details from education funds
  const allLiquidityDetails = educationFundPolicies.flatMap(
    (policy) => policy.liquidity_details || []
  );

  // Calculate tax exempt amount (up to cap only)
  let taxExemptAmount = 0;

  allLiquidityDetails.forEach((detail) => {
    // If it's up to cap (tax exempt), add to tax exempt amount
    if (detail.matched_deposit_cap === LiquidityMatchedDepositCap.UP_TO_CAP) {
      taxExemptAmount += detail.balance_amount || 0;
    }
  });

  return { totalAmount, taxExemptAmount };
}

/**
 * Calculates capital (liquid) fund information including total amount and already liquidated amount.
 * @param products - List of products.
 * @returns Object with totalAmount and alreadyLiquidAmount
 */
static getCapitalFundInfo(
  products: Product[]
): { totalAmount: number; alreadyLiquidAmount: number } {
  // Get all policies from liquid products
  const liquidPolicies = MimshakService.getPoliciesOrAccountsFromLiquidProducts(products);

  // Calculate total accumulated amount using existing utility function
  const totalAmount = MimshakService.calculateTotalPolicySumCapital(liquidPolicies);

  // Calculate already liquidated amount (past liquidity)
  const alreadyLiquidAmount = MimshakService.getPastLiquidityAmountSum(liquidPolicies);

  return { totalAmount, alreadyLiquidAmount };
}

/**
 * Get pension fund breakdown by balance type (capital, paying, non-paying)
 * Uses the same calculation method as ProductsBalanceMetrics
 * @param products - Array of pension products
 * @returns Object with capitalAmount, annuityAmount (paying) and nonAnnuityAmount (non-paying)
 */
static getPensionFundInfo(
  products: Product[]
): { capitalAmount: number; annuityAmount: number; nonAnnuityAmount: number } {
  let capitalAmount = 0;
  let annuityAmount = 0;
  let nonAnnuityAmount = 0;

  // Calculate amounts using the same method as ProductsBalanceMetrics
  products.forEach((product) => {
    // Calculate capital amount (הון)
    const capital = MimshakService.calculateTotalsByType(
      product.policies_or_accounts,
      LiquidityPeriodBalanceType.CAPITAL
    ) || 0;

    // Calculate paying pension amount (קצבה משלמת)
    const payingAmount = MimshakService.calculateTotalsByType(
      product.policies_or_accounts,
      LiquidityPeriodBalanceType.PAYING_PENSION
    ) || 0;

    // Calculate non-paying pension amount (משיכה חד פעמית)
    const nonPayingAmount = MimshakService.calculateTotalsByType(
      product.policies_or_accounts,
      LiquidityPeriodBalanceType.NON_PAYING_PENSION
    ) || 0;

    capitalAmount += capital;
    annuityAmount += payingAmount;
    nonAnnuityAmount += nonPayingAmount;
  });

  return { capitalAmount, annuityAmount, nonAnnuityAmount };
}

static calculateWeightedAverageNetReturnForPolicies(
  policies: PolicyOrAccount[],
  balanceType: LiquidityPeriodBalanceType
): number {
  // סינון הפוליסות שכוללות accumulated_savings
  const filteredPolicies = policies.filter(
    (policy) =>
      policy.accumulated_savings &&
      policy.accumulated_savings.some(
        (saving) => saving.savings_type_code === balanceType
      )
  );

  // אם אין פוליסות מתאימות, החזר 0
  if (filteredPolicies.length === 0) {
    return 0;
  }

  // חישוב הסכום המשוקלל והסכום הכולל של כל הפוליסות
  const { totalWeightedReturn, totalSavings } = filteredPolicies.reduce(
    (acc, policy) => {
      const filteredSavings = policy.accumulated_savings.filter(
        (saving) => saving.savings_type_code === balanceType
      );

      const policyWeightedReturn = filteredSavings.reduce(
        (sum, saving) =>
          sum + saving.total_savings * (policy.net_return_rate || 0),
        0
      );

      const policyTotalSavings = filteredSavings.reduce(
        (sum, saving) => sum + saving.total_savings,
        0
      );

      return {
        totalWeightedReturn: acc.totalWeightedReturn + policyWeightedReturn,
        totalSavings: acc.totalSavings + policyTotalSavings,
      };
    },
    { totalWeightedReturn: 0, totalSavings: 0 }
  );

  // אם סך הכל הסכומים הוא 0, החזר 0 למניעת חלוקה ב-0
  if (totalSavings === 0) {
    return 0;
  }

  // חישוב ממוצע משוקלל
  return totalWeightedReturn / totalSavings;
}
static totalNetReturnAmount(
  policies: PolicyOrAccount[],
  balanceType: LiquidityPeriodBalanceType
): { totalAmount: number; zeroOrEmptyCount: number } {
  // סינון הפוליסות שכוללות accumulated_savings
  const filteredPolicies = policies.filter(
    (policy) =>
      policy.accumulated_savings &&
      policy.accumulated_savings.some(
        (saving) => saving.savings_type_code === balanceType
      )
  );

  // אם אין פוליסות מתאימות, החזר ערכים ריקים
  if (filteredPolicies.length === 0) {
    return { totalAmount: 0, zeroOrEmptyCount: 0 };
  }

  // צבירת הסכום הכולל וספירת הפוליסות עם net_return_amount ריק או שווה ל-0
  const result = filteredPolicies.reduce(
    (acc, policy) => {
      const netReturnAmount = policy.net_return_amount || 0;

      // צבירת הסכום
      acc.totalAmount += netReturnAmount;

      // בדיקה אם הערך ריק או שווה ל-0
      if (!policy.net_return_amount || netReturnAmount === 0) {
        acc.zeroOrEmptyCount += 1;
      }

      return acc;
    },
    { totalAmount: 0, zeroOrEmptyCount: 0 }
  );

  return result;
}


static calculateWeightedAverageNetReturn(
  policy: PolicyOrAccount,
  balanceType: LiquidityPeriodBalanceType
): number {
  // בודק אם יש accumulated_savings רלוונטיים לחישוב
  if (!policy.accumulated_savings || policy.accumulated_savings.length === 0) {
    return 0;
  }

  // סינון לפי balanceType
  const filteredSavings = policy.accumulated_savings.filter(
    (saving) => saving.savings_type_code === balanceType
  );

  // אם אין נתונים מתאימים לאחר הסינון, החזר 0
  if (filteredSavings.length === 0) {
    return 0;
  }

  // חישוב סכום משוקלל וסך הכל הסכומים
  const totalWeightedReturn = filteredSavings.reduce(
    (sum, saving) =>
      sum + saving.total_savings * (policy.net_return_rate || 0),
    0
  );

  const totalSavings = filteredSavings.reduce(
    (sum, saving) => sum + saving.total_savings,
    0
  );

  // אם סך הכל הסכומים הוא 0, החזר 0 למניעת חלוקה ב-0
  if (totalSavings === 0) {
    return 0;
  }

  // חישוב ממוצע משוקלל
  return totalWeightedReturn / totalSavings;
}
static calculateWeightedExposureFromPaths(
  paths: InvestmentPathDetail[],
  fundData: FundDataBasic[]
): {
  stockExposure: { average: number; amount: number };
  currencyExposure: { average: number; amount: number };
  foreignExposure: { average: number; amount: number };
  excludedPaths: { pathCode: number; excludedAmount: number }[];
} {
  let totalAccumulatedAmount = 0;
  let totalStockExposureAmount = 0;
  let totalCurrencyExposureAmount = 0;
  let totalForeignExposureAmount = 0;

  let weightedStockExposure = 0;
  let weightedCurrencyExposure = 0;
  let weightedForeignExposure = 0;

  const excludedPaths: { pathCode: number; excludedAmount: number }[] = [];

  paths.forEach((path) => {
    const fund = fundData.find((f) => f.fund_id === path.path_code);

    if (fund && fund.total_assets && path.accumulated_amount) {
      const amount = path.accumulated_amount;
      totalAccumulatedAmount += amount;

      if (fund.stock_market_exposure !== undefined) {
        const stock = fund.stock_market_exposure / fund.total_assets;
        totalStockExposureAmount += stock * amount;
        weightedStockExposure += stock * amount;
      }

      if (fund.foreign_currency_exposure !== undefined) {
        const currency = fund.foreign_currency_exposure / fund.total_assets;
        totalCurrencyExposureAmount += currency * amount;
        weightedCurrencyExposure += currency * amount;
      }

      if (fund.foreign_exposure !== undefined) {
        const foreign = fund.foreign_exposure / fund.total_assets;
        totalForeignExposureAmount += foreign * amount;
        weightedForeignExposure += foreign * amount;
      }
    } else {
      excludedPaths.push({
        pathCode: path.path_code,
        excludedAmount: path.accumulated_amount ?? 0,
      });
    }
  });

  return {
    stockExposure: {
      average: totalAccumulatedAmount ? weightedStockExposure / totalAccumulatedAmount : 0,
      amount: totalStockExposureAmount,
    },
    currencyExposure: {
      average: totalAccumulatedAmount ? weightedCurrencyExposure / totalAccumulatedAmount : 0,
      amount: totalCurrencyExposureAmount,
    },
    foreignExposure: {
      average: totalAccumulatedAmount ? weightedForeignExposure / totalAccumulatedAmount : 0,
      amount: totalForeignExposureAmount,
    },
    excludedPaths,
  };
}


static calculateWeightedExposure = (
  policies: PolicyOrAccount[],
  fundData: FundDataBasic[]
): {
  stockExposure: { average: number; amount: number };
  currencyExposure: { average: number; amount: number };
  foreignExposure: { average: number; amount: number };
  excludedPaths: { pathCode: number; excludedAmount: number }[];
} => {
  const allPaths = policies.flatMap((p) => p.investment_paths);
  return this.calculateWeightedExposureFromPaths(allPaths, fundData);
};
static calculateWeightedReturnRatePension(
  policies: PolicyOrAccount[]
): {
  weightedReturnRate: number;
  excludedPolicies: { policyNumber: string; excludedAmount: number }[];
} {
  let totalAccumulatedAmount = 0;
  let weightedReturnSum = 0;

  const excludedPolicies: { policyNumber: string; excludedAmount: number }[] = [];

  policies.forEach((policy) => {
    const accumulatedAmount = this.calculatePolicyTotalAmountPension(policy);

    if (policy.net_return_rate !== undefined && accumulatedAmount > 0) {
      totalAccumulatedAmount += accumulatedAmount;
      weightedReturnSum += policy.net_return_rate * accumulatedAmount;
    } else {
      excludedPolicies.push({
        policyNumber: policy.policy_number,
        excludedAmount: accumulatedAmount,
      });
    }
  });

  return {
    weightedReturnRate: totalAccumulatedAmount
      ? weightedReturnSum / totalAccumulatedAmount
      : 0,
    excludedPolicies,
  };
}

static calculateWeightedYield(
  policies: PolicyOrAccount[],
  fundData: FundDataBasic[]
): {
  threeYearYield: number;
  fiveYearYield: number;
  yearToDateYield: number;
  monthlyYield: number;
  excludedPaths: { pathCode: number; excludedAmount: number }[];
} {
  let totalAccumulatedAmount = 0;

  let weightedThreeYearYield = 0;
  let weightedFiveYearYield = 0;
  let weightedYearToDateYield = 0;
  let weightedMonthlyYield = 0;

  const excludedPaths: { pathCode: number; excludedAmount: number }[] = [];

  policies.forEach((policy) => {
    policy.investment_paths.forEach((path) => {
      const fund = fundData.find((f) => f.fund_id === path.path_code);

      if (fund && path.accumulated_amount) {
        const accumulatedAmount = path.accumulated_amount;
        totalAccumulatedAmount += accumulatedAmount;

        if (fund.yield_trailing_3_yrs !== undefined) {
          weightedThreeYearYield += fund.yield_trailing_3_yrs * accumulatedAmount;
        }

        if (fund.yield_trailing_5_yrs !== undefined) {
          weightedFiveYearYield += fund.yield_trailing_5_yrs * accumulatedAmount;
        }

        if (fund.year_to_date_yield !== undefined) {
          weightedYearToDateYield += fund.year_to_date_yield * accumulatedAmount;
        }

        if (fund.monthly_yield !== undefined) {
          weightedMonthlyYield += fund.monthly_yield * accumulatedAmount;
        }
      } else {
        excludedPaths.push({ pathCode: path.path_code, excludedAmount: path.accumulated_amount || 0 });
      }
    });
  });

  return {
    threeYearYield: totalAccumulatedAmount ? weightedThreeYearYield / totalAccumulatedAmount : 0,
    fiveYearYield: totalAccumulatedAmount ? weightedFiveYearYield / totalAccumulatedAmount : 0,
    yearToDateYield: totalAccumulatedAmount ? weightedYearToDateYield / totalAccumulatedAmount : 0,
    monthlyYield: totalAccumulatedAmount ? weightedMonthlyYield / totalAccumulatedAmount : 0,
    excludedPaths,
  };
}


/**
 * Simulates the backward history of a fund's balance given a final amount,
 * accumulation fees, monthly yields, and other parameters.
 *
 * @param finalAmount - The final balance of the fund at the current month.
 * @param accumulationFee - The percentage fee deducted monthly for the previous month (e.g., 0.05 for 0.05%).
 * @param monthlyYields - An array of objects, each containing:
 *   - `year` (number): The year for the yield.
 *   - `month` (number): The month for the yield (1 = January, 12 = December).
 *   - `monthlyYield` (number): The monthly yield percentage (e.g., 0.13 for 0.13%).
 * @param currentYear - The current year of the simulation's final month.
 * @param currentMonth - The current month of the simulation's final month.
 * @param monthlyDeposit - The fixed monthly deposit amount (default is 0).
 * @param depositFee - The percentage fee applied to each deposit (default is 0, e.g., 1 for 1%).
 * @returns An array of objects, each containing:
 *   - `year` (number): The year of the calculation.
 *   - `month` (number): The month of the calculation.
 *   - `balance` (number): The fund's balance at the end of the month.
 *   - `gain` (number): The gain achieved during the month.
 *   - `accumulationFeePaid` (number): The amount paid as an accumulation fee for the previous month.
 *   - `depositFeePaid` (number): The amount paid as a deposit fee for the current month.
 */
static simulateFundHistoryBackward(
  finalAmount: number,
  accumulationFee: number,
  monthlyYields: { year: number; month: number; monthlyYield: number }[],
  currentYear: number,
  currentMonth: number,
  monthlyDeposit: number = 0,
  depositFee: number = 0
): { year: number; month: number; balance: number; gain: number; accumulationFeePaid: number; depositFeePaid: number }[] {
  const results: { year: number; month: number; balance: number; gain: number; accumulationFeePaid: number; depositFeePaid: number }[] = [];

  // Convert percentages to decimal values
  const accFeeFactor = 1 - accumulationFee / 100;
  const depositFeeFactor = depositFee / 100;

  let balance = finalAmount;

  // Add the final balance as the first entry
  results.push({
    year: currentYear,
    month: currentMonth,
    balance,
    gain: 0,
    accumulationFeePaid: 0,
    depositFeePaid: 0,
  });

  // Calculate backwards for the remaining months
  for (let i = monthlyYields.length - 1; i >= 0; i--) {
    const { year, month, monthlyYield } = monthlyYields[i];

    // Calculate the deposit fee for the current month
    const depositFeePaid = monthlyDeposit * depositFeeFactor;

    // Reverse deposit (subtract net deposit from balance)
    const netDeposit = monthlyDeposit - depositFeePaid;
    balance -= netDeposit;

    const previousBalance = balance;

    // Calculate accumulation fee for the previous balance
    const accumulationFeePaid = balance * (accumulationFee / 100);

    // Reverse accumulation fee
    balance /= accFeeFactor;

    // Reverse yield adjustment using the specific yield for the current month
    const yieldFactor = 1 + monthlyYield / 100;
    balance /= yieldFactor;

    // Calculate gain
    const gain = previousBalance - balance;

    // Add the result for this month
    results.push({
      year,
      month,
      balance,
      gain,
      accumulationFeePaid,
      depositFeePaid,
    });
  }

  // Reverse the results to show chronological order
  return results.reverse();
}


// הפונקציה מדמה היסטוריית קרן חיסכון או השקעה בהתבסס על ערך סופי נתון,
//  דמי ניהול, הפקדות חודשיות ותשואות חודשיות, תוך כדי חישוב הפוך (מהסוף להתחלה) עבור כל חודש.
// INPUTS:
// finalAmount: הסכום הסופי של הקרן (נניח בעתיד).
// accumulationFee: אחוז דמי ניהול מצטברים (למשל, 0.05 עבור 0.05%).
// months: מספר החודשים לחישוב היסטוריית הקרן.
// deposits: מערך הפקדות חודשיות (כולל מספר חודש וסכום הפקדה לאותו חודש).
// yields: מערך תשואות חודשיות (כולל מספר חודש ואחוז תשואה לחודש).

// OUTPUT IS A LIST of{ 
//   month: number;   // החודש הנוכחי (מספר חודשים לפני הסוף, מתחיל ב-1)
//   balance: number; // יתרת הקרן בתום החודש
//   gain: number;    // הרווח החודשי (כתוצאה מתשואה או הפחתת דמי ניהול)
//   deposit: number; // סכום ההפקדה החודשית
// }
static simulateFundHistoryWithArrays(
  finalAmount: number,
  accumulationFee: number, // Percentage, e.g., 0.05 for 0.05%
  months: number,
  deposits: { month: number; amount: number }[], // Array of deposit objects
  yields: { month: number; rate: number }[] // Array of yield objects
): { month: number; balance: number; gain: number; deposit: number }[] {
  const results: { month: number; balance: number; gain: number; deposit: number }[] = [];

  // Convert percentages to decimal values
  const accFeeFactor = 1 - accumulationFee / 100;

  let balance = finalAmount;

  // Map deposits and yields for quick lookup
  const depositMap = new Map(deposits.map((d) => [d.month, d.amount]));
  const yieldMap = new Map(yields.map((y) => [y.month, y.rate]));

  // Default deposit and yield values
  const defaultDeposit = deposits.length > 0 ? deposits[0].amount : 0;
  const defaultYield = yields.length > 0 ? yields[0].rate : 0;

  // Add the final balance as the first entry (month n)
  results.push({ month: months, balance, gain: 0, deposit: defaultDeposit });

  // Calculate backwards for the remaining months
  for (let i = months - 1; i >= 1; i--) {
    // Get deposit and yield for this month or default values
    const monthlyDeposit = depositMap.get(i) ?? defaultDeposit;
    const monthlyYield = yieldMap.get(i) ?? defaultYield;

    // Reverse the deposit
    balance -= monthlyDeposit;

    const previousBalance = balance;

    // Reverse accumulation fee
    balance /= accFeeFactor;

    // Reverse yield adjustment
    const yieldFactor = 1 + monthlyYield / 100;
    balance /= yieldFactor;

    // Calculate gain
    const gain = previousBalance - balance;

    // Add the result for this month
    results.push({ month: i, balance, gain, deposit: monthlyDeposit });
  }

  // Reverse the results to show chronological order
  return results.reverse();
}

static prepareAndSimulatePolicyHistoryPension = (
  policy: PolicyOrAccount,
  yields: { month: number; rate: number }[], // מגיע מבחוץ
  months?: number // אפשרי לקבל מבחוץ
): { month: number; balance: number; gain: number; deposit: number }[] => {
  // 1. חישוב finalAmount
  const finalAmount = MimshakService.calculatePolicyTotalAmountPension(policy);

  // 2. הגדרת accumulationFee
  const accumulationFee =
    policy.investment_paths && policy.investment_paths.length > 0
      ? policy.investment_paths[0].management_fee_on_savings || 0
      : 0;

  // 3. עיבוד הפקדות ל-deposits
  const groupedDeposits = MimshakService.groupDepositsByPaymentPeriod(policy.deposits || []);
  const deposits = groupedDeposits.map((group) => ({
    month: group.period,
    amount: group.totalSum,
  }));

  // 4. חישוב מספר החודשים (months) אם לא נמסר
  let totalMonths = months;
  if (!totalMonths && groupedDeposits.length > 0) {
    const firstPeriod = groupedDeposits[0].period; // התקופה הראשונה
    const lastPeriod = groupedDeposits[groupedDeposits.length - 1].period; // התקופה האחרונה

    // המרת התקופה הראשונה והאחרונה ל-ISO Date
    const firstDate = new Date(
      `${Math.floor(firstPeriod / 100)}-${String(firstPeriod % 100).padStart(2, "0")}-01`
    );
    const lastDate = new Date(
      `${Math.floor(lastPeriod / 100)}-${String(lastPeriod % 100).padStart(2, "0")}-01`
    );

    // חישוב מספר החודשים בין שתי התאריכים
    totalMonths =
      (lastDate.getFullYear() - firstDate.getFullYear()) * 12 +
      (lastDate.getMonth() - firstDate.getMonth()) +
      1;
  }

  if (!totalMonths) {
    throw new Error("Unable to calculate the number of months.");
  }

  // 5. קריאה ל-simulateFundHistoryWithArrays
  return MimshakService.simulateFundHistoryWithArrays(
    finalAmount,
    accumulationFee,
    totalMonths,
    deposits,
    yields
  );
};
static calculateTotalExpectedMonthlyPension(policy: PolicyOrAccount): number {
  if (!policy.pension_fund_details || policy.pension_fund_details.length === 0) {
      return 0;
  }

  return policy.pension_fund_details.reduce((total, fund) => {
   
      const expectedMonthlyPension = fund.expected_monthly_pension || 0; // בדיקה אם הערך קיים
      return total + expectedMonthlyPension;
  }, 0);
}
static calculateTotalMonthlyPensionForPolicies(policies: PolicyOrAccount[]): number {
  if (!policies || policies.length === 0) {
      return 0;
  }

  return policies.reduce((total, policy) => {
      const policyTotal = MimshakService.calculateTotalExpectedMonthlyPension(policy);
      return total + policyTotal;
  }, 0);
}

/**
 * Aggregates amounts by fund ID (path_code) from policies
 * Uses mergeDuplicateInvestmentPaths to merge duplicate paths before aggregating
 * @param policies - List of policies or accounts
 * @returns Map of fund_id to total amount
 */
static aggregateAmountsByFundId(policies: PolicyOrAccount[]): Map<number, number> {
  const fundAmountMap = new Map<number, number>();

  policies.forEach(policy => {
    // מיזוג מסלולים כפולים לפני חישוב הסכומים
    const mergedPaths = MimshakService.mergeDuplicateInvestmentPaths(policy.investment_paths || []);

    mergedPaths.forEach(path => {
      const fundId = path.path_code;
      const amount = path.accumulated_amount || 0;

      fundAmountMap.set(fundId, (fundAmountMap.get(fundId) || 0) + amount);
    });
  });

  return fundAmountMap;
}

/**
 * Aggregates amounts by fund ID from products
 * @param products - List of products
 * @returns Map of fund_id to total amount
 */
static aggregateAmountsByFundIdFromProducts(products: Product[]): Map<number, number> {
  const policies = MimshakService.getAllPoliciesOrAccounts(products);
  return MimshakService.aggregateAmountsByFundId(policies);
}
static getProductByPolicyNumber(
  productType: number,
  policyNumber: string,
  products: Product[]
): Product | undefined {
  return products.find(
    (product) =>
      product.details.product_type === productType &&
      product.policies_or_accounts.some(
        (policy) => policy.policy_number === policyNumber
      )
  );
}
static getProductByPolicyNumber2(
  products: Product[],
  policyNumber: string,
 
): Product | undefined {
  return products.find(
    (product) =>
    
      product.policies_or_accounts.some(
        (policy) => policy.policy_number === policyNumber
      )
  );
}
  /**
   * Formats a number to a localized string.
   * @param num - The number to format.
   * @returns Localized string representation of the number.
   */
  static formatNumber(num: number): string {
    return num.toLocaleString("he-IL", { minimumFractionDigits: 2 });
  }
  static calculateTotalTransferOut(
    fundActions: FundAction[],
    referenceDate: string
  ): [number, TransferOutMap[]] | null {
    if (!fundActions || fundActions.length === 0) {
      return null;
    }

    const refDate = new Date(referenceDate);
    let totalTransferOut = 0;
    const handlerMap: Record<string, number> = {};

    for (const action of fundActions) {
      if (
        action.action_type_code !== ActionTypeCode.WITHDRAWAL &&
        action.value_date
      ) {
        try {
          const valueDate = new Date(action.value_date);

          if (
            valueDate.getFullYear() === refDate.getFullYear() &&
            valueDate.getMonth() === refDate.getMonth()
          ) {
            // ✅ סכום כללי של סכומי המשיכה
            const withdrawalAmount = action.withdrawal_amount || 0;
            totalTransferOut += withdrawalAmount;

            // ✅ עדכון לפי receiving_handler_code
            if (action.receiving_handler_code) {
              if (!handlerMap[action.receiving_handler_code]) {
                handlerMap[action.receiving_handler_code] = 0;
              }

              handlerMap[action.receiving_handler_code] += withdrawalAmount;
            }
          }
        } catch (error) {
          console.error(`Invalid date format: ${action.value_date}`, error);
          continue;
        }
      }
    }

    // ✅ אם אין נתונים שסוכמו → מחזירים null
    if (totalTransferOut === 0) {
      return null;
    }

    // ✅ הפיכת מילון לרשימה של אובייקטים
    const transferOutToMap: TransferOutMap[] = Object.entries(handlerMap).map(
      ([handlerCode, amount]) => ({
        handler_code: handlerCode,
        amount,
      })
    );

    return [totalTransferOut, transferOutToMap];
  }

  static calculateTotalTransferIn(
    fundDetails: FundTransferDetails[],
    referenceDate: string
  ): [number, TransferOutMap[]] | null {
    if (!fundDetails || fundDetails.length === 0) {
      return null;
    }

    const refDate = new Date(referenceDate);
    let totalTransferOut = 0;
    const handlerMap: Record<string, TransferOutMap> = {};

    for (const detail of fundDetails) {
      if (detail.deposit_date) {
        try {
          const depositDate = new Date(detail.deposit_date);

          // ✅ בדיקה אם זה באותו חודש ושנה כמו התאריך הנוכחי
          if (
            depositDate.getFullYear() === refDate.getFullYear() &&
            depositDate.getMonth() === refDate.getMonth()
          ) {
            const depositAmount = detail.deposit_amount_paid || 0;
            totalTransferOut += depositAmount;

            if (detail.transferring_corporation) {
              // ✅ אם עדיין לא נרשם – נוסיף אובייקט חדש
              if (!handlerMap[detail.transferring_corporation]) {
                handlerMap[detail.transferring_corporation] = {
                  handler_code: detail.transferring_corporation,
                  amount: 0,
                  // transferring_fund_id: detail.transferring_fund_id || 0,
                  // transferring_fund_name: detail.transferring_fund_name || 'לא ידוע',
                };
              }

              // ✅ נוסיף לסכום
              handlerMap[detail.transferring_corporation].amount += depositAmount;
            }
          }
        } catch (error) {
          console.error(`Invalid date format: ${detail.deposit_date}`, error);
          continue;
        }
      }
    }

    if (totalTransferOut === 0) {
      return null;
    }

    // ✅ הפיכת מילון לרשימה של אובייקטים
    const transferOutToMap: TransferOutMap[] = Object.values(handlerMap);

    return [totalTransferOut, transferOutToMap];
  }
//#endregion

  public static detectGender(mimshakData: MimshakData[]): 'f' | 'm' | null {
    for (const md of mimshakData) {
      for (const product of md.data?.products ?? []) {
        const gender = product.client?.gender?.trim();
        if (gender === Gender.FEMALE) return 'f';
        if (gender === Gender.MALE) return 'm';
      }
    }
    return null;
  }
}
export interface TransferOutMap {
  handler_code: string;
  amount: number;
  // transferring_fund_id: number;
  // transferring_fund_name: string;
}
export interface LoanWithPolicy {
  loan: LoanDetails;
  policy: PolicyOrAccount;
}

export interface LoanFromMimshakData {
  loan: LoanDetails;
  handlerCode: string;
  policyNumber: string;
  productType: number;
}

